/**
 * CTRL_09 Plugin - Data Residency Compliance
 * Custom UI with geographic data storage visualization and compliance tracking
 * Extends GenericConfigPanel with region map and compliance status
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Alert,
  Table,
  Tag,
  Space,
  Statistic,
  List,
} from 'antd';
import {
  GlobalOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Data Residency Dashboard Component
 */
function DataResidencyDashboard({ config, projectId }) {
  const [regions, setRegions] = useState([]);
  const [compliance, setCompliance] = useState({
    compliant: 0,
    violations: 0,
  });

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchResidencyData();
    }
  }, [config, projectId]);

  const fetchResidencyData = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setCompliance({
        compliant: 15420,
        violations: 3,
      });
      setRegions([
        {
          region: 'us-east-1',
          records: 8500,
          status: 'compliant',
          allowed: true,
        },
        {
          region: 'eu-west-1',
          records: 6920,
          status: 'compliant',
          allowed: true,
        },
        {
          region: 'ap-southeast-1',
          records: 3,
          status: 'violation',
          allowed: false,
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      render: (region) => (
        <Space>
          <EnvironmentOutlined />
          {region}
        </Space>
      ),
    },
    {
      title: 'Records',
      dataIndex: 'records',
      key: 'records',
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const color = status === 'compliant' ? 'green' : 'red';
        const icon = status === 'compliant' ? <CheckCircleOutlined /> : <WarningOutlined />;
        return (
          <Tag color={color} icon={icon}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Allowed',
      dataIndex: 'allowed',
      key: 'allowed',
      render: (allowed) => (allowed ? 'Yes' : 'No'),
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Data Residency Compliance Disabled"
        description="Enable to enforce geographic data storage restrictions."
        type="info"
        showIcon
      />
    );
  }

  const totalRecords = compliance.compliant + compliance.violations;
  const complianceRate = totalRecords > 0 ? ((compliance.compliant / totalRecords) * 100).toFixed(2) : 100;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Compliance Statistics */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Compliant Records"
              value={compliance.compliant}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Violations"
              value={compliance.violations}
              prefix={<WarningOutlined />}
              valueStyle={{ color: colors.status.fail }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Compliance Rate"
              value={complianceRate}
              suffix="%"
              prefix={<GlobalOutlined />}
              valueStyle={{ color: complianceRate >= 99 ? colors.status.pass : colors.status.warning }}
            />
          </Card>
        </Col>
      </Row>

      {/* Region Breakdown */}
      <div>
        <h4>Data by Region</h4>
        <Table
          columns={columns}
          dataSource={regions}
          rowKey="region"
          size="small"
          pagination={false}
        />
      </div>

      {/* Allowed Regions */}
      <Card size="small" title="Configured Allowed Regions">
        <List
          size="small"
          dataSource={config?.allowed_regions || []}
          renderItem={(region) => (
            <List.Item>
              <Space>
                <EnvironmentOutlined style={{ color: colors.status.pass }} />
                <Tag color="green">{region}</Tag>
              </Space>
            </List.Item>
          )}
        />
      </Card>
    </Space>
  );
}

/**
 * CTRL_09 Plugin Component
 */
function CTRL_09_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    allowed_regions: ['us-east-1', 'eu-west-1'],
  });

  return (
    <div>
      {/* Residency Dashboard */}
      <DataResidencyDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_09_Plugin;
